import { gql, NetworkStatus, useQuery } from '@apollo/client'
import get from 'lodash/get'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistTradeForGuestQuery,
  ArtistTradeForGuestQueryVariables,
  ArtistTradeQuery,
  ArtistTradeQueryVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistTradeQuery,
  FetchNewArtistTradeQueryVariables,
  TradeBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user: currentUser, userClaims } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistTradeQuery,
    FetchNewArtistTradeQueryVariables
  >({
    query: FETCH_NEW_TRADE,
    variables: { artistId, pageInput: { limit: 10 } },
    fields: [
      {
        fieldName: 'tradeNegotiationsMyRequests',
        fragment: TradeBoxFragmentDoc,
      },
      { fieldName: 'artistTradeNegotiations', fragment: TradeBoxFragmentDoc },
      {
        fieldName: 'tradeMyCandidates',
        fragment: TradeBoxFragmentDoc,
        uniqByKey: 'node.tradeNegotiation.__ref',
        generateNode(cache, data) {
          return {
            __typename: 'TradeCandidate',
            tradeNegotiation: cache.writeFragment({
              fragment: TradeBoxFragmentDoc,
              data: get(data, 'tradeNegotiation'),
            }),
          }
        },
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, fetchMore, networkStatus } = useQuery<
    ArtistTradeQuery,
    ArtistTradeQueryVariables
  >(ARTIST_TRADE, {
    variables: { artistId },
    skip: !currentUser,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const isNextFetch = !!data

  const {
    data: dataForGuest,
    networkStatus: networkStatusForGuest,
    fetchMore: fetchMoreForGuest,
  } = useQuery<ArtistTradeForGuestQuery, ArtistTradeForGuestQueryVariables>(
    QUERY_FOR_GUEST,
    {
      variables: { artistId },
      skip: !!currentUser,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        isFirstLoadRef.current = false
        if (data.artist) {
          currentArtistVar(data.artist)
        }
      },
    }
  )
  const isNextFetchForGuest = !!dataForGuest

  const pageInfo = (data ?? dataForGuest)?.artistTradeNegotiations?.pageInfo
  const myCandidateTrades =
    (data ?? dataForGuest)?.tradeMyCandidates?.edges.map(
      ({ node }) => node.tradeNegotiation
    ) ?? []
  const myRequestTrades =
    (data ?? dataForGuest)?.tradeNegotiationsMyRequests?.edges.map(
      ({ node }) => node
    ) ?? []
  const myInProgressTrades = [...myRequestTrades, ...myCandidateTrades]

  return {
    state: {
      loading:
        (networkStatus === NetworkStatus.loading && !isNextFetch) ||
        (networkStatusForGuest === NetworkStatus.loading &&
          !isNextFetchForGuest),
      refetching:
        networkStatus === NetworkStatus.fetchMore ||
        networkStatusForGuest === NetworkStatus.fetchMore,
      hasNextPage: pageInfo?.hasNextPage ?? false,
      artistId,
      artistName: data?.artist?.displayName || '',
      // 取引中のトレード
      myInProgressTrades,
      // みんなのトレード
      trades:
        data?.artistTradeNegotiations ??
        dataForGuest?.artistTradeNegotiations ??
        null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      user: data?.user ?? null,
      liveStreamId: (data ?? dataForGuest)?.artist?.currentLive?.id,
    },
    actions: {
      async onFetchMore() {
        if (!currentUser) {
          await fetchMoreForGuest({
            variables: {
              pageInput: {
                cursor: pageInfo?.endCursor ?? null,
              },
            },
          })
          return
        }
        await fetchMore({
          variables: {
            pageInput: {
              cursor: pageInfo?.endCursor ?? null,
            },
          },
        })
      },
    },
  }
})

const ARTIST_TRADE = gql`
  query ArtistTrade($artistId: ID!, $pageInput: PageInput) {
    tradeNegotiationsMyRequests(
      artistId: $artistId
      status: [in_progress, broken_off, dropped, agreed]
    ) {
      edges {
        node {
          ...TradeBox
        }
      }
    }
    tradeMyCandidates(
      artistId: $artistId
      status: [in_progress, rejected, accepted, expired, dropped]
    ) {
      edges {
        node {
          ... on TradeCandidate {
            tradeNegotiation {
              ...TradeBox
            }
          }
        }
      }
    }
    artistTradeNegotiations(artistId: $artistId, pageInput: $pageInput) {
      edges {
        node {
          ...TradeBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${TradeBoxFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_TRADE = gql`
  query FetchNewArtistTrade($artistId: ID!, $pageInput: PageInput) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    tradeNegotiationsMyRequests(
      artistId: $artistId
      status: [in_progress, broken_off, dropped, agreed]
    ) {
      edges {
        node {
          ...TradeBox
        }
      }
    }
    tradeMyCandidates(
      artistId: $artistId
      status: [in_progress, rejected, accepted, expired, dropped]
    ) {
      edges {
        node {
          ... on TradeCandidate {
            tradeNegotiation {
              ...TradeBox
            }
          }
        }
      }
    }
    artistTradeNegotiations(artistId: $artistId, pageInput: $pageInput) {
      edges {
        node {
          ...TradeBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${TradeBoxFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query ArtistTradeForGuest($artistId: ID!, $pageInput: PageInput) {
    tradeNegotiationsMyRequests(
      artistId: $artistId
      status: [in_progress, broken_off, dropped, agreed]
    ) {
      edges {
        node {
          ...TradeBox
        }
      }
    }
    tradeMyCandidates(
      artistId: $artistId
      status: [in_progress, rejected, accepted, expired, dropped]
    ) {
      edges {
        node {
          ... on TradeCandidate {
            tradeNegotiation {
              ...TradeBox
            }
          }
        }
      }
    }
    artistTradeNegotiations(artistId: $artistId, pageInput: $pageInput) {
      edges {
        node {
          ...TradeBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${TradeBoxFragmentDoc}
`
